import { AUDIT_ACTIONS } from '../audit/retention';
import { fill, t, type StringKey } from '../strings';
import { routingViaPhrase } from './approvalGate';

/**
 * 🔒 A document's own saves as its History reads them (T928, a quote's edits
 * in its History; decision 1696). A create, an edit and a floor
 * acknowledgement each write one `AuditEvent` row, and the row's
 * `changedFields?` names each field moved with the words a person read at the
 * time, so the sentence never re-reads a record that has since changed.
 */
export const DOCUMENT_HISTORY_FIELDS = {
  total: 'total',
  lines: 'lines',
  paymentTerms: 'paymentTerms',
  quoteDate: 'quoteDate',
  expiryDate: 'expiryDate',
  displayMode: 'displayMode',
  billTo: 'billTo',
  shipTo: 'shipTo',
  deal: 'deal',
  shipmentPreference: 'shipmentPreference',
  customerNotes: 'customerNotes',
  termsOverride: 'termsOverride',
  /** 🔒 The customer's own purchase order number on a Quote, from and to in words (T1720; decision 2290). */
  customerPo: 'customerPo',
  /** A floor acknowledgement's floor, in words: the document floor, or the line it names. */
  floor: 'floor',
  /** A floor acknowledgement's shortfall, as money words. */
  shortfall: 'shortfall',
} as const;

/** The order an edit row's clauses read in, and each field's label. */
const EDIT_FIELDS: readonly (readonly [string, StringKey])[] = [
  [DOCUMENT_HISTORY_FIELDS.total, 'history.document.total'],
  [DOCUMENT_HISTORY_FIELDS.lines, 'history.document.lines'],
  [DOCUMENT_HISTORY_FIELDS.paymentTerms, 'history.document.paymentTerms'],
  [DOCUMENT_HISTORY_FIELDS.quoteDate, 'history.document.quoteDate'],
  [DOCUMENT_HISTORY_FIELDS.expiryDate, 'history.document.expiryDate'],
  [DOCUMENT_HISTORY_FIELDS.displayMode, 'history.document.displayMode'],
  [DOCUMENT_HISTORY_FIELDS.billTo, 'history.document.billTo'],
  [DOCUMENT_HISTORY_FIELDS.shipTo, 'history.document.shipTo'],
  [DOCUMENT_HISTORY_FIELDS.deal, 'history.document.deal'],
  [DOCUMENT_HISTORY_FIELDS.shipmentPreference, 'history.document.shipmentPreference'],
  [DOCUMENT_HISTORY_FIELDS.customerNotes, 'history.document.customerNotes'],
  [DOCUMENT_HISTORY_FIELDS.termsOverride, 'history.document.termsOverride'],
  [DOCUMENT_HISTORY_FIELDS.customerPo, 'history.document.customerPo'],
];

export const DOCUMENT_HISTORY_ACTIONS: readonly string[] = [AUDIT_ACTIONS.documentCreated, AUDIT_ACTIONS.documentEdited, AUDIT_ACTIONS.documentFloorAcknowledged];

interface Change {
  field: string;
  from: unknown;
  to: unknown;
}

function entries(changedFields: unknown): Change[] {
  return Array.isArray(changedFields) ? changedFields.filter((f): f is Change => typeof f === 'object' && f !== null && typeof (f as { field?: unknown }).field === 'string') : [];
}

function words(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * 🔒 A field's clause: from and to where the row carried both as words, and
 * that it changed where it carried neither, as a site or a note is recorded.
 */
function clause(label: string, change: Change): string {
  const from = words(change.from);
  const to = words(change.to);
  if (to === null && from === null) return fill(t('history.audit.documentFieldChanged'), { field: label });
  return fill(t('history.audit.documentFieldMoved'), { field: label, from: from ?? '—', to: to ?? '—' });
}

/** The History sentence for a document's create, edit or floor acknowledgement, or null for any other action. */
export function documentHistoryLine(action: string, changedFields: unknown): string | null {
  if (!DOCUMENT_HISTORY_ACTIONS.includes(action)) return null;
  const changes = entries(changedFields);
  if (action === AUDIT_ACTIONS.documentCreated) return t('history.audit.documentCreated');
  if (action === AUDIT_ACTIONS.documentFloorAcknowledged) {
    const floor = words(changes.find((c) => c.field === DOCUMENT_HISTORY_FIELDS.floor)?.to) ?? t('history.document.documentFloor');
    const shortfall = words(changes.find((c) => c.field === DOCUMENT_HISTORY_FIELDS.shortfall)?.to) ?? '—';
    return fill(t('history.audit.documentFloorAcknowledged'), { floor, shortfall });
  }
  const clauses: string[] = [];
  for (const [field, key] of EDIT_FIELDS) {
    const change = changes.find((c) => c.field === field);
    if (change) clauses.push(clause(t(key), change));
  }
  return clauses.length === 0 ? t('history.audit.documentEdited') : `${t('history.audit.documentEdited')}: ${clauses.join(', ')}`;
}

/** 🔒 The phrase a cleared hold reads as; the other ways come from `routingViaPhrase` (T1890; decision 2482). */
const APPROVAL_VIA_DETAIL: Readonly<Record<string, StringKey>> = {
  editCleared: 'history.audit.approvalVia.editCleared',
};

/**
 * 🔒 How an approval was routed, as the History row's detail beneath *Approval
 * routed* (T1890; decision 2482). A routing that held the document wrote its own
 * sentence, who it went to and why, and that sentence is read back as written.
 * A routing that held nothing wrote none, so its way names why nobody's approval
 * is needed. 💰 The row keeps no amount, so that phrase names none.
 */
export function approvalRoutedHistoryDetail(reason: string | null, changedFields: unknown): string | null {
  if (reason) return `${reason.charAt(0).toUpperCase()}${reason.slice(1)}`;
  const via = words(entries(changedFields).find((c) => c.field === 'via')?.to);
  // 🔒 The three ways that held nothing are read from approvalGate's own sentences, so the row and the block cannot drift (T2003).
  const key = via ? APPROVAL_VIA_DETAIL[via] : undefined;
  const phrase = via === 'rails' || via === 'noRule' || via === 'bypass' ? routingViaPhrase(via) : key ? t(key) : null;
  /*
   * 🔒 Decision 3280 (T2269): a routing that held nothing still names the warnings it carried, ahead of the way it
   * went, as a held routing's sentence names them; a credit warning on an order inside the rails is otherwise unread.
   */
  const carried = entries(changedFields).find((c) => c.field === 'warnings')?.to;
  const named = Array.isArray(carried) ? carried.filter((w): w is string => typeof w === 'string' && w.trim() !== '') : [];
  if (named.length === 0) return phrase;
  const lead = named.join('; ');
  const opened = `${lead.charAt(0).toUpperCase()}${lead.slice(1)}`;
  return phrase ? `${opened} — ${phrase.charAt(0).toLowerCase()}${phrase.slice(1)}` : opened;
}
